// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.1
// updated  : 2026-06-20
// protected: NO
// changelog: v1.1 (P0) - hapus tombol "Arsipkan" manual (PDF kini auto-arsip di backend).
// =============================================================================
// Daftar & detail Invoice V2 (read, RLS region).
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, ReceiptText, FileText, Search, X } from "lucide-react";
import { useV2Auth } from "@/v2/V2AuthContext";
import { rupiah, openPdf } from "@/v2/components/helpers";
import { TableScroll } from "@/v2/components/TableScroll";
import { ListPager, loadStoredPageSize } from "@/v2/components/ListPager";

export default function InvoicePage() {
  const { api } = useV2Auth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  // Paginasi + pencarian (Slice S-F).
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => loadStoredPageSize("invoices"));
  const [meta, setMeta] = useState({ total: 0, pages: 1 });
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const listTopRef = useRef(null);
  const pageRef = useRef(1);

  const loadList = useCallback(async (p, size = pageSize) => {
    const target = p || pageRef.current || 1;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (from) params.set("date_from", from);
      if (to) params.set("date_to", to);
      params.set("page", target);
      params.set("page_size", size);
      const r = await api(`/api/v2/invoices?${params.toString()}`);
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        setInvoices(d.items || []);
        setMeta({ total: d.total || 0, pages: d.pages || 1 });
        setPage(d.page || target); pageRef.current = d.page || target;
      } else setInvoices([]);
    } finally { setLoading(false); }
  }, [api, q, from, to, pageSize]);

  // Pencarian/filter berubah -> kembali ke halaman 1.
  useEffect(() => { loadList(1); }, [loadList]);

  const goPage = (p) => {
    loadList(p);
    if (listTopRef.current) listTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const changePageSize = (s) => { setPageSize(s); loadList(1, s); };
  const reset = () => { setQ(""); setFrom(""); setTo(""); };

  return (
    <div className="space-y-5" data-testid="v2-invoice-page">
      <h2 className="text-2xl font-semibold flex items-center gap-2"><ReceiptText className="w-6 h-6" />Invoice</h2>
      <Card className="p-4 rounded-2xl">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs">Dari Tanggal</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="invoice-date-from" />
          </div>
          <div>
            <Label className="text-xs">Sampai Tanggal</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="invoice-date-to" />
          </div>
          <div>
            <Label className="text-xs">Cari No. Invoice</Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="cth: INV-2606" className="pl-8" data-testid="invoice-search" />
            </div>
          </div>
          <Button variant="ghost" onClick={reset} data-testid="invoice-reset"><X className="w-4 h-4 mr-1" />Reset</Button>
        </div>
      </Card>
      <Card className="p-5 rounded-2xl" ref={listTopRef}>
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        <TableScroll>
          <Table>
            <TableHeader><TableRow>
              <TableHead>No</TableHead><TableHead>Store</TableHead><TableHead>Tipe</TableHead>
              <TableHead className="text-right">PPN</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead>Bayar</TableHead><TableHead>Merchandize</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {invoices.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">Belum ada invoice.</TableCell></TableRow>}
              {invoices.map((inv) => (
                <TableRow key={inv.id} data-testid="v2-invoice-row">
                  <TableCell className="font-mono text-xs">{inv.invoice_no}</TableCell>
                  <TableCell>{inv.store}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge variant={inv.txn_type === "Compliment" ? "secondary" : "default"}>{inv.txn_type}</Badge>
                      {inv.cancelled_at && (
                        <Badge variant="destructive" data-testid="v2-invoice-cancelled-tag"
                          title={inv.cancellation_doc_no ? `Dibatalkan lewat ${inv.cancellation_doc_no}` : "Dibatalkan"}>
                          DIBATALKAN{inv.cancellation_doc_no ? ` · ${inv.cancellation_doc_no}` : ""}
                        </Badge>
                      )}
                      {inv.credit_sale && !inv.cancelled_at && (
                        <Badge data-testid="v2-invoice-credit-tag"
                          variant={inv.credit_status === "PAID" ? "success"
                            : inv.credit_status === "FORCED" ? "forced"
                            : "warning"}>
                          {inv.credit_status === "PAID" ? "Bayar Nanti · Lunas"
                            : inv.credit_status === "FORCED" ? "Bayar Nanti · Force-Bill"
                            : "Bayar Nanti · Belum Lunas"}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums" data-testid="v2-invoice-ppn">
                    {inv.tax_amount > 0
                      ? <span title={`PPN ${inv.tax_percent}%`}>{rupiah(inv.tax_amount)}</span>
                      : <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{rupiah(inv.total)}</TableCell>
                  <TableCell className="text-xs">{inv.payment_method}</TableCell>
                  <TableCell>{inv.lines?.length || 0}</TableCell>
                  <TableCell className="text-right space-x-1 whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openPdf(api, "invoice", inv.id, inv.invoice_no)} data-testid="v2-invoice-pdf" title="Unduh PDF">
                      <FileText className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableScroll>
        <ListPager
          page={page} pageSize={pageSize} total={meta.total} pages={meta.pages} loading={loading}
          onPageChange={goPage} onPageSizeChange={changePageSize}
          storageKey="invoices" testid="invoice-pager"
        />
      </Card>
    </div>
  );
}
